// ============= Types =============

export interface RemovableFileObject {
  fileName?: string;
  name?: string;
  url?: string;
  blobUrl?: string;
  blobURL?: string;
}

// ============= Helpers =============

const isNonEmptyString = (value: unknown): value is string =>
  typeof value === 'string' && value.trim().length > 0;

/** Extract a blob URL from a file metadata object. */
export const getBlobUrlFromFileObject = (
  file: RemovableFileObject | null | undefined
): string | undefined => {
  if (!file || typeof file !== 'object') return undefined;
  if (isNonEmptyString(file.blobURL)) return file.blobURL.trim();
  if (isNonEmptyString(file.blobUrl)) return file.blobUrl.trim();
  if (isNonEmptyString(file.url)) return file.url.trim();
  return undefined;
};

/** Extract blob URLs from a form file field value (JSON string, object, or array). */
export const extractBlobUrlsFromFileValue = (value: unknown): string[] => {
  if (!value || value instanceof File) return [];

  let parsed: unknown = value;

  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) return [];
    if (
      (trimmed.startsWith('[') && trimmed.endsWith(']')) ||
      (trimmed.startsWith('{') && trimmed.endsWith('}'))
    ) {
      try {
        parsed = JSON.parse(trimmed);
      } catch {
        return [];
      }
    } else {
      return [];
    }
  }

  if (Array.isArray(parsed)) {
    return parsed
      .map((item) => getBlobUrlFromFileObject(item as RemovableFileObject))
      .filter((url): url is string => Boolean(url));
  }

  if (parsed && typeof parsed === 'object') {
    const url = getBlobUrlFromFileObject(parsed as RemovableFileObject);
    return url ? [url] : [];
  }

  return [];
};

/** Merge and de-dupe blob URLs for pending deletion. */
export const mergePendingDocumentDeletions = (
  existing: unknown,
  nextUrls: string[]
): string[] => {
  const current = Array.isArray(existing)
    ? existing.filter(isNonEmptyString).map((url) => url.trim())
    : [];

  return [...new Set([...current, ...nextUrls.map((url) => url.trim())])];
};

/**
 * Collect blob URLs that should be deleted on submit.
 * Includes explicitly queued removals and invoice files cleared via VAT = no.
 */
export const getDocumentBlobUrlsToDelete = (
  data: Record<string, unknown> | undefined
): string[] => {
  if (!data) return [];

  const tracked = mergePendingDocumentDeletions(
    data.pendingDocumentDeletions,
    []
  );

  const invoiceValue = data.invoiceUpload ?? data.invoice_upload;
  const fromVatNo =
    data.vatInvoiceAttached === 'no'
      ? extractBlobUrlsFromFileValue(invoiceValue)
      : [];

  return mergePendingDocumentDeletions(tracked, fromVatNo);
};
